import {PaymentData, PaymentMethod, PaymentProviderResponse} from './payment';
import {PaymentProvider} from './provider';
import {RecipientStatus} from './recipient';

// =============================================================================
// Health Check Test Types
// =============================================================================

export enum HealthCheckTestType {
  /** Can run anytime with synthetic/minimal data */
  SYNTHETIC = 'synthetic',
  /** Requires real transaction fixtures to test */
  REQUIRES_FIXTURES = 'requires_fixtures',
  /** Cannot be tested automatically - manual verification needed */
  MANUAL_ONLY = 'manual_only',
}

export enum HealthCheckStatus {
  PASSED = 'passed',
  /** Some non-critical tests failed (e.g., webhook), but core functionality works */
  DEGRADED = 'degraded',
  FAILED = 'failed',
  SKIPPED = 'skipped',
  NOT_SUPPORTED = 'not_supported',
  NO_FIXTURES = 'no_fixtures',
}

// =============================================================================
// Individual Test Results
// =============================================================================

export interface HealthCheckTestResult {
  status: HealthCheckStatus;
  testType: HealthCheckTestType;
  duration?: number; // milliseconds
  error?: string;
  details?: Record<string, unknown>;
}

export interface HealthCheckConnectivityResult extends HealthCheckTestResult {
  authenticated?: boolean;
  tokenExpiry?: Date;
}

export interface HealthCheckBalanceResult extends HealthCheckTestResult {
  balance?: {
    available: number;
    blocked?: number;
    total?: number;
  };
}

export interface HealthCheckWebhookResult extends HealthCheckTestResult {
  configured?: boolean;
  url?: string;
  events?: string[];
  lastResponse?: {
    statusCode: number;
    body: unknown;
  };
}

export interface HealthCheckPaymentResult extends HealthCheckTestResult {
  method: PaymentMethod;
  paymentData?: PaymentData | null;
  providerResponse?: PaymentProviderResponse<unknown, unknown> | null;
  /** ID of the test invoice created (will be auto-canceled) */
  testInvoiceId?: number;
}

export interface HealthCheckWithdrawalResult extends HealthCheckTestResult {
  /** Withdrawal tests typically require fixtures */
  fixtureUsed?: string;
}

export interface HealthCheckRetrieveResult extends HealthCheckTestResult {
  transactionId?: string;
  retrievedAt?: Date;
}

// =============================================================================
// Balance Reconciliation
// =============================================================================

export interface WalletBalance {
  available: number;
  pending: number;
  withheld: number;
  total: number;
}

export interface BalanceReconciliation {
  /** Internal wallet balance (from our database) */
  internal: WalletBalance;
  /** Provider-reported balance */
  provider: WalletBalance | null;
  /** Discrepancy between internal and provider (provider - internal) */
  discrepancy: {
    available: number;
    total: number;
  } | null;
  /** Whether balances match within tolerance */
  isReconciled: boolean;
  /** Tolerance used for comparison (in currency units BRL, e.g. 1 = R$1.00) */
  tolerance?: number;
  /** @deprecated Use tolerance (BRL) */
  toleranceCents?: number;
  /** Last time provider balance was fetched */
  providerFetchedAt?: Date;
}

// =============================================================================
// Provider Health Check Summary
// =============================================================================

export interface ProviderHealthCheckResult {
  provider: PaymentProvider;
  timestamp: Date;
  overallStatus: HealthCheckStatus;

  tests: {
    connectivity?: HealthCheckConnectivityResult;
    balance?: HealthCheckBalanceResult;
    webhook?: HealthCheckWebhookResult;
    payments: Record<PaymentMethod, HealthCheckPaymentResult>;
    withdrawals?: HealthCheckWithdrawalResult;
    retrieves?: HealthCheckRetrieveResult;
  };

  /** Balance reconciliation between internal wallet and provider */
  reconciliation?: BalanceReconciliation;

  summary: {
    total: number;
    passed: number;
    failed: number;
    skipped: number;
    /** Tests for methods not supported by this provider */
    notSupported: number;
  };
}

// =============================================================================
// Recipient Health Check (Full Result)
// =============================================================================

export interface RecipientHealthCheck {
  id: number;
  name: string;
  document: string;
  status: RecipientStatus;
  timestamp: Date;

  /** Internal wallet info */
  wallet?: WalletBalance;

  /** All active providers for this recipient */
  providers: ProviderHealthCheckResult[];

  /** Overall summary across all providers */
  summary: {
    totalProviders: number;
    healthyProviders: number;
    degradedProviders: number;
    failedProviders: number;
  };

  /** Overall reconciliation status */
  reconciliationStatus: 'reconciled' | 'discrepancy' | 'unknown';
}

// =============================================================================
// DNS Record Validation Result
// =============================================================================

export interface DnsRecordValidationResult extends HealthCheckTestResult {
  recordName: string;
  recordType: string;
  expectedValue: string;
  actualValues?: string[];
  isValid: boolean;
}

// =============================================================================
// Domain Health Check
// =============================================================================

export interface DomainHealthCheck {
  id: number;
  name: string;
  identifier: string;
  timestamp: Date;

  /** DNS records validation */
  dnsRecords: {
    overallStatus: HealthCheckStatus;
    records: DnsRecordValidationResult[];
    summary: {
      total: number;
      passed: number;
      failed: number;
      skipped: number;
    };
  };
}

// =============================================================================
// Health Check Configuration
// =============================================================================

export interface HealthCheckConfig {
  /** Skip payment generation tests */
  skipPaymentTests?: boolean;
  /** Skip webhook configuration tests */
  skipWebhookTests?: boolean;
  /** Skip balance reconciliation */
  skipReconciliation?: boolean;
  /** Payment methods to test (default: all enabled except credit card) */
  paymentMethods?: PaymentMethod[];
  /** Use cached results if available and fresh (seconds) */
  cacheMaxAge?: number;
  /** Tolerance for balance reconciliation in cents (default: 100 = R$1.00) */
  reconciliationToleranceCents?: number;
}

// =============================================================================
// Test Fixtures (for tests requiring real data)
// =============================================================================

export interface HealthCheckFixture {
  id: string;
  provider: PaymentProvider;
  type: 'withdrawal' | 'credit_card' | 'refund';
  recipientId: number;
  createdAt: Date;
  expiresAt?: Date;

  /** Sanitized data from a real successful transaction */
  fixtureData: {
    transactionId?: string;
    amount?: number;
    bankAccount?: {
      pixKey?: string;
      holderName?: string;
      document?: string;
    };
    creditCard?: {
      tokenId?: string;
      lastFourDigits?: string;
      brand?: string;
    };
  };
}

// =============================================================================
// Provider Health Check Interface
// =============================================================================

/**
 * Interface that provider services can implement to support health checks
 */
export interface ProviderHealthCheckable {
  /**
   * Test connectivity/authentication
   */
  testConnectivity?(): Promise<HealthCheckConnectivityResult>;

  /**
   * Test balance retrieval
   */
  testBalance?(): Promise<HealthCheckBalanceResult>;

  /**
   * Test webhook configuration
   */
  testWebhook?(): Promise<HealthCheckWebhookResult>;

  /**
   * Get supported payment methods for health check
   */
  getSupportedPaymentMethods?(): PaymentMethod[];

  /**
   * Get health check test type for withdrawals
   */
  getWithdrawalTestType?(): HealthCheckTestType;
}

// =============================================================================
// Legacy Types (for backwards compatibility)
// =============================================================================

/** @deprecated Use ProviderHealthCheckResult instead */
export interface RecipientHealthCheckProvider {
  general: {
    webhook: {
      status: boolean;
      url: string;
      lastResponse?: RecipientHealthCheckResponseInfo;
    };
    info: {
      status: boolean;
      url: string;
      lastResponse?: RecipientHealthCheckResponseInfo;
    };
  };
  payment: {
    [key in PaymentMethod]: {
      status: boolean;
      paymentData?: PaymentData | null;
      paymentMetadata?: PaymentProviderResponse<unknown, unknown> | null;
    };
  };
}

/** @deprecated Use HealthCheckTestResult.details instead */
export interface RecipientHealthCheckResponseInfo {
  statusCode: number;
  body: string;
  headers: Record<string, string>;
}
